/**
 * AuthenticatorSection — the Owner's authenticator app (v53, 29 Oct 2026).
 * Stuart: "We can do authenticator codes but I don't want to have to input one
 * every time I log in" — so sign-in never asks for one; a code is needed only
 * to set a real person's password (SetPasswordModal).
 * Set-up: staff password → we email a 6-digit code → scan the QR code → type
 * the emailed code and a code from the app. Backend: /api/platform/me/
 * authenticator (GET, /setup, /confirm; rules in backend/src/lib/staffTotp.js).
 * The secret is shown only during set-up and never kept in the page after.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export default function AuthenticatorSection() {
  const { platformAxios } = usePlatformAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState('idle'); // idle | password | scan
  const [password, setPassword] = useState('');
  const [currentCode, setCurrentCode] = useState(''); // QA S1: the working app vouches for a new one
  const [setup, setSetup] = useState(null); // { secret, qr }
  const [emailCode, setEmailCode] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [justOn, setJustOn] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['platform-authenticator'],
    queryFn: () => platformAxios.get('/me/authenticator').then(r => r.data),
  });

  const start = useMutation({
    mutationFn: () => platformAxios.post('/me/authenticator/setup', status?.enabled ? { password, currentCode } : { password }).then(r => r.data),
    onSuccess: (data) => {
      setPassword(''); setCurrentCode('');
      setSetup({ secret: data.secret, qr: data.qr }); setStep('scan'); setError('');
      start.reset(); // QA N3: the secret lives only in `setup`, cleared when done
    },
    onError: (err) => { setCurrentCode(''); setError(err.response?.data?.error || 'Could not start the set-up'); },
  });

  const confirm = useMutation({
    mutationFn: () => platformAxios.post('/me/authenticator/confirm', { emailCode, code }).then(r => r.data),
    onSuccess: () => {
      setSetup(null); setEmailCode(''); setCode(''); setStep('idle'); setError(''); setJustOn(true);
      start.reset();
      qc.invalidateQueries({ queryKey: ['platform-authenticator'] });
    },
    onError: (err) => { setCode(''); setError(err.response?.data?.error || 'Could not switch it on'); },
  });

  const cancel = () => {
    setStep('idle'); setPassword(''); setCurrentCode(''); setSetup(null); setEmailCode(''); setCode(''); setError('');
    start.reset(); confirm.reset();
  };
  const six = (v) => /^\d{6}$/.test(v.replace(/\s/g, ''));
  const digits = (v) => v.replace(/[^\d ]/g, '');

  if (isLoading) return null;

  return (
    <div className="authenticator-card">
      {step === 'idle' && (
        <>
          {status?.enabled ? (
            <p className="authenticator-status is-on">
              <ShieldCheck size={18} /> On since {fmtDate(status.enabledAt)}.
            </p>
          ) : (
            <p className="authenticator-status">Not set up yet.</p>
          )}
          {justOn && (
            <p className="authenticator-done"><CheckCircle2 size={16} /> Switched on. We’ve emailed you to say so.</p>
          )}
          <p className="authenticator-intro">
            You only need a code when you set a member’s password from Clubs. Signing in never asks for one.
            Works with Google Authenticator, Microsoft Authenticator, 1Password and similar apps.
            Switching to a new phone needs a code from the old one.
          </p>
          <button type="button" className="btn-secondary" onClick={() => { setJustOn(false); setError(''); setStep('password'); }}>
            {status?.enabled ? 'Set up on a new phone' : 'Set up authenticator'}
          </button>
        </>
      )}

      {step === 'password' && (
        <form onSubmit={(e) => { e.preventDefault(); if (password && (!status?.enabled || six(currentCode)) && !start.isPending) start.mutate(); }}>
          <p className="authenticator-intro">
            Enter your staff password{status?.enabled ? ' and a code from the app you use now' : ''}. We’ll email you a code to go with it.
          </p>
          {error && <div className="error-box" style={{ marginBottom: 12 }}><AlertCircle size={16} /><p>{error}</p></div>}
          <div className="form-group">
            <label htmlFor="auth-pw">Your staff password</label>
            <input id="auth-pw" type="password" autoComplete="current-password" autoFocus
              value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {status?.enabled && (
            <div className="form-group">
              <label htmlFor="auth-current-code">Code from the authenticator app you use now</label>
              <input id="auth-current-code" className="authenticator-code-input" inputMode="numeric" autoComplete="one-time-code"
                maxLength={7} placeholder="123456" value={currentCode} onChange={(e) => setCurrentCode(digits(e.target.value))} />
              <p className="test-pw-hint">Lost that phone? It has to be reset by hand — ask your developer.</p>
            </div>
          )}
          <div className="authenticator-actions">
            <button type="button" className="btn-secondary" onClick={cancel}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!password || (status?.enabled && !six(currentCode)) || start.isPending}>
              {start.isPending ? 'Checking…' : 'Next'}
            </button>
          </div>
        </form>
      )}

      {step === 'scan' && setup && (
        <form className="ph-no-capture" onSubmit={(e) => { e.preventDefault(); if (six(emailCode) && six(code) && !confirm.isPending) confirm.mutate(); }}>
          <ol className="authenticator-steps">
            <li>Open your authenticator app and add an account by scanning this code.</li>
          </ol>
          <img className="authenticator-qr ph-no-capture" src={setup.qr} alt="QR code for your authenticator app" width={200} height={200} />
          <p className="authenticator-key">
            Can’t scan? Enter this key: <code className="ph-no-capture">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          </p>
          <ol className="authenticator-steps" start={2}>
            <li>Enter the code we’ve just emailed you, and the code your app shows.</li>
          </ol>
          {error && <div className="error-box" style={{ marginBottom: 12 }}><AlertCircle size={16} /><p>{error}</p></div>}
          <div className="authenticator-codes">
            <div className="form-group">
              <label htmlFor="auth-email-code">Code from the email</label>
              <input id="auth-email-code" className="authenticator-code-input" inputMode="numeric" autoComplete="off"
                maxLength={7} placeholder="123456" value={emailCode} onChange={(e) => setEmailCode(digits(e.target.value))} />
            </div>
            <div className="form-group">
              <label htmlFor="auth-app-code">Code from the app</label>
              <input id="auth-app-code" className="authenticator-code-input" inputMode="numeric" autoComplete="one-time-code"
                maxLength={7} placeholder="123456" value={code} onChange={(e) => setCode(digits(e.target.value))} />
            </div>
          </div>
          <div className="authenticator-actions">
            <button type="button" className="btn-secondary" onClick={cancel}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!six(emailCode) || !six(code) || confirm.isPending}>
              {confirm.isPending ? 'Checking…' : 'Switch on'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
